import { Icon } from '@iconify/react'
import { useCallback, useMemo, useRef, useState } from 'react'
import * as Handlebars from 'handlebars'
import _ from 'lodash'
import { renderGoTemplate } from '../api'

type Engine = 'text' | 'html' | 'lodash' | 'handlebars'

const engineMeta: Record<Engine, { label: string; icon: string; hint: string }> = {
  text: { label: 'Go text/template', icon: 'mdi:language-go', hint: 'Go text/template. No HTML escaping.' },
  html: { label: 'Go html/template', icon: 'mdi:language-go', hint: 'Go html/template. Auto-escapes HTML.' },
  lodash: { label: 'Lodash template', icon: 'mdi:code-json', hint: 'Lodash _.template() — JS in template.' },
  handlebars: { label: 'Handlebars', icon: 'mdi:code-braces', hint: 'Handlebars.js {{mustache}} syntax.' },
}

const engineDefaults: Record<Engine, string> = {
  text: '{{range .Items}}\n  {{.title}}\n{{end}}',
  html: '{{range .Items}}\n  <li>{{.title}} — {{.url}}</li>\n{{end}}',
  lodash: '<% _.forEach(data.Items, function(item) { %>\n<%= item.url %>\n<% }); %>',
  handlebars: '{{#each Items}}\n  {{title}} — {{url}}\n{{/each}}',
}

type DataNode = {
  path: string
  keys: string[]
  isArray: boolean
  sampleValue: unknown
}

/** Walk data to find arrays + objects for button generation. */
function analyzeData(data: unknown, basePath = 'data'): DataNode[] {
  const nodes: DataNode[] = []

  if (Array.isArray(data)) {
    const keys = data.length > 0 && typeof data[0] === 'object' && data[0] !== null
      ? Object.keys(data[0] as Record<string, unknown>)
      : []
    nodes.push({ path: basePath, keys, isArray: true, sampleValue: data[0] })
    if (data.length > 0 && typeof data[0] === 'object' && data[0] !== null) {
      nodes.push(...analyzeData(data[0] as Record<string, unknown>, `${basePath}[0]`))
    }
  } else if (typeof data === 'object' && data !== null) {
    const obj = data as Record<string, unknown>
    const ownKeys = Object.keys(obj)
    nodes.push({ path: basePath, keys: ownKeys, isArray: false, sampleValue: undefined })
    for (const key of ownKeys) {
      const val = obj[key]
      if (Array.isArray(val) || (typeof val === 'object' && val !== null)) {
        nodes.push(...analyzeData(val, `${basePath}.${key}`))
      }
    }
  }

  return nodes
}

/** Build the "for each" wrapper text using actual array path in data. */
function forEachWrapper(engine: Engine, arrayPath: string): string {
  // 'data' (bare array) → 'Items', 'data.results' → 'results', 'data.products' → 'products'
  const ref = arrayPath.replace(/^data\.?/, '') || 'Items'

  switch (engine) {
    case 'text':
    case 'html':
      return `{{range .${ref}}}\n  \n{{end}}`
    case 'lodash':
      return `<% _.forEach(data.${ref}, function(item) { %>\n<% }); %>`
    case 'handlebars':
      return `{{#each ${ref}}}\n  \n{{/each}}`
  }
}

/** Build a nested "for each" wrapper relative to current item (used inside a loop). */
function nestedForEachWrapper(engine: Engine, relativePath: string): string {
  switch (engine) {
    case 'text':
    case 'html':
      return `{{range .${relativePath}}}\n  \n{{end}}`
    case 'lodash': {
      return `<% _.forEach(item.${relativePath}, function(sub) { %>\n<% }); %>`
    }
    case 'handlebars': {
      // Inside {{#each Items}}, dot-separated nested path uses slash in Handlebars
      return `{{#each ${relativePath.replace(/\./g, '/')}}}\n  \n{{/each}}`
    }
  }
}

/** Build template snippet for a property access, per engine. */
function propertySnippet(engine: Engine, propPath: string, loopVar: string | null): string {
  const parts = propPath.split('.')

  switch (engine) {
    case 'text':
    case 'html': {
      if (loopVar) return `{{.${propPath}}}`
      return `{{.${propPath}}}`
    }
    case 'lodash': {
      const varName = loopVar || 'item'
      if (parts[0] === 'attributes' && parts.length > 1) {
        const nested = parts.slice(1).map(p => `["${p}"]`).join('')
        return `<%= ${varName}.attributes${nested} %>`
      }
      return `<%= ${varName}.${propPath} %>`
    }
    case 'handlebars': {
      if (parts[0] === 'attributes' && parts.length > 1) {
        return `{{${parts.join('/')}}}`
      }
      return `{{${propPath}}}`
    }
  }
}

/** Given cursor position and template text, detect if cursor is inside a loop block. */
function findLoopContext(
  text: string,
  cursorPos: number,
  engine: Engine,
  dataNodes: DataNode[],
): { collectionName: string | null; fields: string[] } {
  if (cursorPos < 0) return { collectionName: null, fields: [] }

  const before = text.slice(0, cursorPos)

  let openRE: RegExp
  let closeRE: RegExp
  if (engine === 'text' || engine === 'html') {
    openRE = /\{\{range\s*\.?(\w+)\}\}/g
    closeRE = /\{\{end\}\}/g
  } else if (engine === 'handlebars') {
    openRE = /\{\{#each\s+(\w+)\}\}/g
    closeRE = /\{\{\/each\}\}/g
  } else {
    openRE = /<% _\.forEach\(([^,]+)/g
    closeRE = /<% \}\); %>/g
  }

  type Event = { pos: number; type: 'open' | 'close'; name?: string }
  const events: Event[] = []

  let m: RegExpExecArray | null
  while ((m = openRE.exec(before)) !== null) {
    events.push({ pos: m.index, type: 'open', name: m[1].trim() })
  }
  while ((m = closeRE.exec(before)) !== null) {
    events.push({ pos: m.index, type: 'close' })
  }
  events.sort((a, b) => a.pos - b.pos)

  const stack: string[] = []
  for (const e of events) {
    if (e.type === 'open') {
      stack.push(e.name ?? '')
    } else {
      if (stack.length > 0) stack.pop()
    }
  }

  const collectionName = stack.length > 0 ? stack[stack.length - 1] : null
  if (!collectionName) return { collectionName: null, fields: [] }

  // Map collection name to a DataNode to get item fields
  const normalized = collectionName.replace(/^\./, '').replace(/^data\./, '')
  for (const node of dataNodes) {
    const nodeName = node.path.replace(/^data\.?/, '')
    if (nodeName === normalized || node.path === collectionName || node.path === `data.${normalized}`) {
      if (node.isArray) {
        if (typeof node.sampleValue === 'object' && node.sampleValue !== null) {
          const fields = Object.keys(node.sampleValue as Record<string, unknown>)
          return { collectionName: normalized, fields }
        }
        return { collectionName: normalized, fields: node.keys }
      }
    }
  }

  return { collectionName: normalized, fields: [] }
}

function collectLeafPaths(obj: Record<string, unknown>, prefix: string, acc: Set<string>) {
  for (const [key, val] of Object.entries(obj)) {
    const path = prefix ? `${prefix}.${key}` : key
    if (val !== null && typeof val === 'object' && !Array.isArray(val)) {
      collectLeafPaths(val as Record<string, unknown>, path, acc)
    } else {
      acc.add(path)
    }
  }
}

type Props = {
  data: unknown
  template?: string
  onTemplateChange?: (tpl: string) => void
  initialEngine?: Engine
  fileName?: string
}

export function ExportPanel({
  data,
  template: controlledTemplate,
  onTemplateChange,
  initialEngine = 'lodash',
  fileName = 'export.txt',
}: Props) {
  const isControlled = controlledTemplate !== undefined
  const [localTemplate, setLocalTemplate] = useState(engineDefaults[initialEngine])
  const template = isControlled ? controlledTemplate : localTemplate

  const setTemplate = useCallback(
    (tpl: string | ((prev: string) => string)) => {
      if (isControlled) {
        const next = typeof tpl === 'function' ? tpl(controlledTemplate) : tpl
        onTemplateChange?.(next)
      } else {
        setLocalTemplate(tpl)
      }
    },
    [isControlled, controlledTemplate, onTemplateChange],
  )

  const [engine, setEngine] = useState<Engine>(initialEngine)
  const [output, setOutput] = useState('')
  const [error, setError] = useState('')
  const [rendering, setRendering] = useState(false)
  const [cursorPos, setCursorPos] = useState(-1)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const dataNodes = useMemo(() => analyzeData(data), [data])

  // All array nodes for "for each" buttons
  const arrayNodes = useMemo(() => dataNodes.filter((n) => n.isArray), [dataNodes])

  // Shallow keys from first array element (fallback when not in loop)
  const fallbackItemKeys = useMemo(() => {
    if (arrayNodes.length === 0) return []
    return arrayNodes[0].keys
  }, [arrayNodes])

  // ALL leaf paths (shallow + nested) from the data
  const allPropPaths = useMemo(() => {
    const paths = new Set<string>()
    for (const node of dataNodes) {
      if (node.isArray) {
        if (typeof node.sampleValue === 'object' && node.sampleValue !== null) {
          collectLeafPaths(node.sampleValue as Record<string, unknown>, '', paths)
        }
      } else {
        for (const key of node.keys) {
          paths.add(key)
        }
      }
    }
    return [...paths].sort()
  }, [dataNodes])

  // Cursor-aware loop context
  const loopCtx = useMemo(
    () => findLoopContext(template, cursorPos, engine, dataNodes),
    [template, cursorPos, engine, dataNodes],
  )

  // Shallow fields to show in buttons (from loop context or fallback)
  const activeFields = useMemo(() => {
    if (loopCtx.collectionName && loopCtx.fields.length > 0) {
      return { fields: loopCtx.fields, isLoopContext: true, loopVar: loopCtx.collectionName }
    }
    return { fields: fallbackItemKeys, isLoopContext: arrayNodes.length > 0, loopVar: null }
  }, [loopCtx, fallbackItemKeys, arrayNodes.length])

  // Nested leaf paths relevant to current loop context (the deeper paths beyond shallow keys)
  const nestedPaths = useMemo(() => {
    if (loopCtx.collectionName) {
      // Inside loop → nested paths from the loop's array element
      const loopNode = dataNodes.find((n) => {
        const nName = n.path.replace(/^data\.?/, '')
        return n.isArray && (nName === loopCtx.collectionName || n.path === `data.${loopCtx.collectionName}`)
      })
      if (loopNode && typeof loopNode.sampleValue === 'object' && loopNode.sampleValue !== null) {
        const nested = new Set<string>()
        collectLeafPaths(loopNode.sampleValue as Record<string, unknown>, '', nested)
        // Return only paths that are NOT in the shallow fields (those already shown as primary buttons)
        return [...nested].filter((p) => !loopCtx.fields.includes(p)).sort()
      }
      return []
    }
    // Outside loop → all nested paths not in fallbackItemKeys
    return allPropPaths.filter((p) => !fallbackItemKeys.includes(p))
  }, [loopCtx, dataNodes, allPropPaths, fallbackItemKeys])

  // Child arrays within the current loop context item (for nested for-each buttons)
  const loopChildArrays = useMemo(() => {
    if (!loopCtx.collectionName) return []

    // Find the loop's DataNode
    let loopNode: DataNode | null = null
    const normalized = loopCtx.collectionName.replace(/^\./, '').replace(/^data\./, '')
    for (const node of dataNodes) {
      const nodeName = node.path.replace(/^data\.?/, '')
      if (node.isArray && (nodeName === normalized || node.path === `data.${normalized}`)) {
        loopNode = node
        break
      }
    }
    // Fallback: "Items" matches first top-level array
    if (!loopNode && arrayNodes.length > 0) {
      loopNode = arrayNodes[0]
    }

    if (!loopNode) return []

    const firstElementPath = loopNode.path + '[0]'
    const children: Array<{ relativePath: string; absolutePath: string; keys: string[] }> = []
    for (const node of dataNodes) {
      if (!node.isArray) continue
      if (node.path === loopNode.path) continue // skip the loop's own array
      if (node.path.startsWith(firstElementPath)) {
        const relativePath = node.path.slice(firstElementPath.length + 1)
        children.push({ relativePath, absolutePath: node.path, keys: node.keys })
      }
    }
    return children
  }, [loopCtx.collectionName, dataNodes, arrayNodes])

  const renderPreview = useCallback(async () => {
    setRendering(true)
    setError('')
    setOutput('')

    try {
      let result: string

      if (engine === 'text' || engine === 'html') {
        const res = await renderGoTemplate({ engine, template, data })
        if (res.error) {
          setError(res.error)
          result = ''
        } else {
          result = res.output
        }
      } else if (engine === 'lodash') {
        // Build context: spread all keys + Items alias (matches Go/server shape)
        const lodashCtx: Record<string, unknown> = {}
        if (Array.isArray(data)) {
          lodashCtx.Items = data
        } else if (data && typeof data === 'object') {
          const obj = data as Record<string, unknown>
          Object.assign(lodashCtx, obj)
          lodashCtx.Items = (obj.results ?? obj.items ?? obj) as unknown
        } else {
          lodashCtx.Items = data
        }
        const compiled = _.template(template)
        result = compiled({ data: lodashCtx }) as string
      } else {
        const compiled = Handlebars.compile(template)
        const ctx: Record<string, unknown> = {}
        if (Array.isArray(data)) {
          ctx.Items = data
        } else if (data && typeof data === 'object') {
          const obj = data as Record<string, unknown>
          Object.assign(ctx, obj)
          ctx.Items = (obj.results ?? obj.items ?? obj) as unknown
        }
        result = compiled(ctx)
      }

      setOutput(result)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      setOutput('')
    } finally {
      setRendering(false)
    }
  }, [engine, template, data])

  const insertAtCursor = (text: string) => {
    const ta = textareaRef.current
    if (!ta) {
      setTemplate((prev) => prev + '\n' + text)
      return
    }
    const start = ta.selectionStart
    const end = ta.selectionEnd
    const before = template.slice(0, start)
    const after = template.slice(end)
    const next = before + text + after
    setTemplate(next)
    requestAnimationFrame(() => {
      ta.focus()
      const pos = start + text.length
      ta.setSelectionRange(pos, pos)
      setCursorPos(pos)
    })
  }

  const downloadExport = () => {
    if (!output) return
    const blob = new Blob([output], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = fileName
    a.click()
    URL.revokeObjectURL(url)
  }

  const switchEngine = (e: Engine) => {
    setEngine(e)
    setTemplate((prev) => {
      const trimmed = prev.trim()
      if (!trimmed || Object.values(engineDefaults).includes(trimmed)) {
        return engineDefaults[e]
      }
      return prev
    })
    setOutput('')
    setError('')
  }

  const handleCursorChange = () => {
    const ta = textareaRef.current
    setCursorPos(ta?.selectionStart ?? -1)
  }

  const hasData = data != null && !(Array.isArray(data) && data.length === 0) && !(typeof data === 'object' && data !== null && !Array.isArray(data) && Object.keys(data).length === 0)

  return (
    <div>
      {/* Engine tabs */}
      <div className="mb-3 flex flex-wrap gap-1">
        {(Object.entries(engineMeta) as [Engine, typeof engineMeta['text']][]).map(([key, meta]) => (
          <button
            key={key}
            className={`flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors ${
              engine === key
                ? 'bg-cyan-600 text-white'
                : 'bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-slate-100'
            }`}
            onClick={() => switchEngine(key)}
          >
            <Icon icon={meta.icon} width="14" height="14" />
            {meta.label}
          </button>
        ))}
      </div>

      {!hasData ? (
        <p className="text-xs text-slate-500">Run a query first to see data for template building.</p>
      ) : (
        <>
          {/* Dynamic context buttons */}
          <div className="mb-2 flex flex-wrap items-center gap-1.5">
            {/* "for each" buttons — only when NOT inside a loop */}
            {!loopCtx.collectionName && arrayNodes.map((node) => {
              const label = node.path.replace(/^data\.?/, '') || 'data'
              return (
                <button
                  key={node.path}
                  className="flex items-center gap-1 rounded bg-amber-900/40 px-2 py-1 text-xs text-amber-300 hover:bg-amber-800/60 transition-colors"
                  onClick={() => insertAtCursor(forEachWrapper(engine, node.path))}
                  title={`Wrap with for-each over ${label}`}
                >
                  <Icon icon="mdi:repeat" width="12" height="12" />
                  for each {label}
                </button>
              )
            })}

            {/* Inside-loop badge */}
            {loopCtx.collectionName && (
              <span className="flex items-center gap-1 rounded bg-amber-900/50 px-2 py-1 text-xs text-amber-300">
                <Icon icon="mdi:repeat" width="12" height="12" />
                inside {loopCtx.collectionName}
              </span>
            )}

            {/* Nested for-each buttons (sub-arrays inside current loop item) */}
            {loopChildArrays.map((child) => (
              <button
                key={child.absolutePath}
                className="flex items-center gap-1 rounded bg-amber-900/40 px-2 py-1 text-xs text-amber-300 hover:bg-amber-800/60 transition-colors"
                onClick={() => insertAtCursor(nestedForEachWrapper(engine, child.relativePath))}
                title={`Nested for-each over ${child.relativePath}`}
              >
                <Icon icon="mdi:repeat" width="12" height="12" />
                for each {child.relativePath}
              </button>
            ))}

            {/* Property key buttons — shallow fields */}
            {activeFields.fields.length > 0 && arrayNodes.length > 0 && (
              <span className="mx-1 self-center text-xs text-slate-600">|</span>
            )}
            {activeFields.fields.map((key) => (
              <button
                key={key}
                className={`rounded px-2 py-1 text-xs font-mono transition-colors ${
                  loopCtx.collectionName
                    ? 'bg-amber-800/40 text-amber-200 hover:bg-amber-700/60'
                    : 'bg-slate-800 text-cyan-300 hover:bg-slate-700'
                }`}
                onClick={() => insertAtCursor(propertySnippet(engine, key, activeFields.loopVar ? '.Items' : null))}
                title={`Insert ${key}`}
              >
                {key}
              </button>
            ))}

            {/* Nested property paths (e.g., attributes.raw_result.hl_name) */}
            {nestedPaths.length > 0 && (
              <>
                <span className="mx-1 self-center text-xs text-slate-600">|</span>
                {nestedPaths.map((path) => (
                  <button
                    key={path}
                    className={`rounded px-2 py-1 text-xs font-mono transition-colors ${
                      loopCtx.collectionName
                        ? 'bg-indigo-900/40 text-indigo-300 hover:bg-indigo-800/60'
                        : 'bg-slate-800/60 text-indigo-300 hover:bg-slate-700'
                    }`}
                    onClick={() => insertAtCursor(propertySnippet(engine, path, activeFields.loopVar ? '.Items' : null))}
                    title={`Insert ${path}`}
                  >
                    {path}
                  </button>
                ))}
              </>
            )}
          </div>

          {/* Template textarea */}
          <textarea
            ref={textareaRef}
            className="field h-24 font-mono text-xs"
            value={template}
            onChange={(e) => {
              setTemplate(e.target.value)
              requestAnimationFrame(() => handleCursorChange())
            }}
            onSelect={handleCursorChange}
            onClick={handleCursorChange}
            onKeyUp={handleCursorChange}
            placeholder={engineDefaults[engine]}
            spellCheck={false}
          />

          {/* Action buttons */}
          <div className="mt-2 flex gap-2">
            <button className="btn text-xs" onClick={() => void renderPreview()} disabled={rendering}>
              <Icon icon={rendering ? 'mdi:loading' : 'mdi:play'} width="14" height="14" className="mr-1 inline" />
              {rendering ? 'Rendering...' : 'Render preview'}
            </button>
            {output && (
              <button className="btn text-xs" onClick={downloadExport}>
                <Icon icon="mdi:download" width="14" height="14" className="mr-1 inline" />
                Download {fileName}
              </button>
            )}
          </div>

          {/* Error */}
          {error && (
            <p className="mt-2 text-xs text-rose-300 whitespace-pre-wrap">{error}</p>
          )}

          {/* Output */}
          {output && (
            <pre className="output mt-2 max-h-40 text-xs">{output}</pre>
          )}
        </>
      )}
    </div>
  )
}
